import React from 'react';
import { useIntl } from 'react-intl';
import { Controller, useFormContext, FieldError } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import AutocompleteVirtualizedBase from 'components/mui/Form/Autocomplete/AutocompleteVirtualizedBase';

import { FieldConfig } from 'pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/types';

type Props = {
  field: FieldConfig;
  formContext: any;
  isEditMode: boolean;
};

const FormField = ({ field, formContext, isEditMode }: Props) => {
  const { formatMessage } = useIntl();
  const {
    control,
    register,
    formState: { errors, touchedFields },
  } = useFormContext();

  const error = errors[field.name] as FieldError | undefined;
  const isTouched = touchedFields[field.name];

  switch (field.type) {
    case 'text': {
      return (
        <TextField
          {...register(field.name)}
          label={formatMessage(field.label)}
          required={field.required}
          autoFocus={field.autoFocus}
          disabled={isEditMode && !field.editable}
          error={!!error && isTouched}
          helperText={
            isTouched && error?.message
              ? formatMessage({ id: error.message, defaultMessage: error.message })
              : undefined
          }
        />
      );
    }

    case 'autocomplete': {
      const options = formContext[field.optionsKey] ?? [];

      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: rhf, fieldState }) => (
            <AutocompleteVirtualizedBase
              options={options}
              value={options.find((o) => o.value === rhf.value) ?? null}
              onChange={(_, option) => rhf.onChange(option?.value)}
              disableClearable={field.disableClearable}
              disabled={isEditMode && !field.editable}
              textFieldProps={{
                label: formatMessage(field.label),
                required: field.required,
                error: !!fieldState.error && fieldState.isTouched,
                helperText: fieldState.isTouched
                  ? formatMessage({ id: fieldState.error?.message, defaultMessage: fieldState.error?.message })
                  : undefined,
              }}
            />
          )}
        />
      );
    }
  }
};

export default FormField;
